import { Container, Graphics, Text } from 'pixi.js';
import type { WheelDial, WheelWedge } from '@goodtime/shared';

/**
 * The rotating wheel disc — 24 coloured wedges + per-wedge glyph
 * labels. Producer brief 2026-05-16: the full wheel-resolve view
 * (previously CSS-in-React) is being ported to Pixi so the canvas
 * owns the spin physics + layout end-to-end. This widget is the
 * disc itself; the hub, rim, avatars, and chips sit in sibling
 * widgets above/around it.
 *
 * <p>Geometry mirrors the legacy React render:
 * <ul>
 *   <li>{@code WEDGE_COUNT} = 24, each wedge sweeps 15° of arc</li>
 *   <li>Wedge 0 starts at world-angle 0 (12 o'clock) and rotates
 *       clockwise, matching the resolver's
 *       {@code wedgeIndexAtAngle} convention</li>
 *   <li>Labels sit at {@code wedgeLabelRadius} from centre, each
 *       rotated to its wedge mid-angle so the number reads radially and
 *       stays pinned to its section as the disc spins</li>
 * </ul>
 *
 * <p>Rendering is driven by {@link setDial}; rotation is exposed via
 * {@link setRotation} so the host scene's spin tween can drive it.
 * The wedge number labels spin with the disc, pinned to their
 * sections (rotated to their own wedge angle) rather than held upright.
 */

const FONT_FAMILY = '"Fredoka", system-ui, -apple-system, sans-serif';
const WEDGE_COUNT = 24;
const WEDGE_ARC_DEG = 360 / WEDGE_COUNT;

export interface WheelResolveDiscOptions {
  /** Outer diameter of the disc in canvas pixels. */
  diameter: number;
  /** Distance from centre to the wedge label baseline. Defaults to
   *  ~38% of the disc radius — matches the legacy CSS layout where
   *  labels sat in the outer third of each wedge. */
  labelRadius?: number;
  /** Font size for the wedge glyph (e.g. "×3", "BUST"). */
  labelFontSize?: number;
}

/** Per-wedge accent colour — mirrors the legacy {@code wedgeAccent}
 *  helper from {@code WheelResolveView.tsx} so the disc reads
 *  identically pre/post-port. COINS climbs through cool → warm
 *  shades with multiplier; effect wedges have their own hues. */
export function wedgeAccentColor(wedge: WheelWedge): number {
  switch (wedge.kind) {
    case 'COINS': {
      const m = wedge.multiplier ?? 0;
      if (m === 0) return 0x5c6bc0;
      if (m <= 2) return 0x9de54e;
      if (m <= 5) return 0x1fc8c8;
      if (m <= 10) return 0x9b7bff;
      if (m <= 25) return 0xff6b9d;
      return 0xffc940;
    }
    case 'BANKRUPT':
      return 0xe8344e;
    case 'SKIP_NEXT':
      return 0x7a5bff;
    case 'EXTRA_ITEM':
      return 0x6bcbff;
    case 'SPECIAL_PRIZE':
      return 0xffc940;
  }
}

/** Short label for in-wedge text. Capped at ~3 chars to fit the
 *  arc width. */
export function wedgeGlyphText(wedge: WheelWedge): string {
  switch (wedge.kind) {
    case 'COINS':
      return `×${wedge.multiplier ?? 0}`;
    case 'BANKRUPT':
      return '✕';
    case 'SKIP_NEXT':
      return '⏭';
    case 'EXTRA_ITEM':
      return '+1';
    case 'SPECIAL_PRIZE':
      return '★';
  }
}

export class WheelResolveDisc extends Container {
  private readonly wedgeGfx: Graphics;
  private readonly labelHost: Container;
  private readonly labels: Text[] = [];
  private readonly opts: Required<WheelResolveDiscOptions>;
  private currentDial: WheelDial | null = null;
  /** World-frame rotation in degrees. The internal Container spins
   *  to this angle; the wedge number labels are pinned to their
   *  sections (each rotated to its own wedge angle) and spin with it. */
  private currentRotationDeg = 0;

  private readonly shadow: Graphics;
  /** Container that rotates WITH the wedges. Public so the host
   *  scene can mount BONUS_WEDGE markers (or any other dial-
   *  anchored overlay) inside it; markers stay pinned to their
   *  wedge as the wheel spins. */
  readonly rotatingOverlay: Container;

  constructor(options: WheelResolveDiscOptions) {
    super();
    this.opts = {
      diameter: options.diameter,
      labelRadius: options.labelRadius ?? options.diameter * 0.38,
      labelFontSize: options.labelFontSize ?? Math.max(12, Math.round(options.diameter * 0.06)),
    };
    // Solid drop-shadow ring so the disc reads as a dimensional
    // surface against the dark room background. Drawn outside the
    // rotating container so the shadow stays stationary as the
    // wheel spins.
    this.shadow = new Graphics();
    this.redrawShadow();
    this.addChild(this.shadow);

    this.wedgeGfx = new Graphics();
    this.addChild(this.wedgeGfx);

    this.labelHost = new Container();
    this.addChild(this.labelHost);

    this.rotatingOverlay = new Container();
    this.addChild(this.rotatingOverlay);
  }

  /** Producer brief 2026-05-16 — resize the disc geometry to a new
   *  diameter, rebuilding the wedge graphics + label positions so
   *  the font stays proportional to the disc rather than getting
   *  blown up by container scaling. The host scene supplies the
   *  label radius + font size to match the legacy
   *  {@code useWheelDims} table; passing them as args avoids
   *  the disc having to know about viewport tiers. */
  setDiameter(
    diameter: number,
    labelRadius?: number,
    labelFontSize?: number,
  ): void {
    const nextLabelRadius = labelRadius ?? diameter * 0.38;
    const nextLabelFontSize =
      labelFontSize ?? Math.max(12, Math.round(diameter * 0.04));
    if (
      diameter === this.opts.diameter &&
      nextLabelRadius === this.opts.labelRadius &&
      nextLabelFontSize === this.opts.labelFontSize
    ) {
      return;
    }
    this.opts.diameter = diameter;
    this.opts.labelRadius = nextLabelRadius;
    this.opts.labelFontSize = nextLabelFontSize;
    this.redrawShadow();
    this.redraw();
  }

  private redrawShadow(): void {
    this.shadow.clear();
  }

  /** Replace the dial composition. Redraws every wedge fill +
   *  glyph. Producer brief 2026-05-16 — drop the dial-reference
   *  identity check; the host re-emits the same dial object across
   *  STAKE → WHEEL_RESOLVE ticks but the disc still needs to
   *  redraw in case a prior {@link setDiameter} cleared the
   *  graphics buffer. Redraw is cheap (24 wedges + 24 labels). */
  setDial(dial: WheelDial | null): void {
    this.currentDial = dial;
    this.redraw();
  }

  /** Update the wheel's rotation. The wedge number labels are pinned to
   *  their sections — each carries its wedge mid-angle as a static
   *  rotation (set in {@link redraw}) and spins WITH the disc via
   *  {@code labelHost.angle}, with NO per-label counter-rotation, so a
   *  number rotates together with its wedge instead of being held upright
   *  (producer 2026-06-10). The {@link rotatingOverlay} also rotates so
   *  dial-anchored hosts (e.g. BONUS_WEDGE markers) stay pinned to their
   *  wedge; those contents still counter-rotate via their own
   *  setCounterRotation API to read upright. */
  setRotation(deg: number): void {
    this.currentRotationDeg = deg;
    this.wedgeGfx.angle = deg;
    this.labelHost.angle = deg;
    this.rotatingOverlay.angle = deg;
  }

  /** Read current rotation — useful for chaining tweens. */
  getRotation(): number {
    return this.currentRotationDeg;
  }

  destroyDisplay(): void {
    this.removeChildren();
  }

  // ---- internal ---------------------------------------------------------

  private redraw(): void {
    this.wedgeGfx.clear();
    // Drop existing labels.
    for (const label of this.labels) label.destroy();
    this.labels.length = 0;
    this.labelHost.removeChildren();

    if (!this.currentDial) return;
    const wedges = this.currentDial.wedges;
    const radius = this.opts.diameter / 2;
    for (let i = 0; i < WEDGE_COUNT; i++) {
      const wedge = wedges[i];
      if (!wedge) continue;
      // Wedge i covers world-angles [i*15°, (i+1)*15°). Pixi's
      // arc() takes radians and uses 0 = +X axis going CCW. We
      // want 0 = +Y axis going CW (the same convention the
      // resolver uses: wedge 0 sits at the 12 o'clock pointer
      // before rotation). So convert: angleRad = -90° + (i * 15°).
      const startRad = ((i * WEDGE_ARC_DEG - 90) * Math.PI) / 180;
      const endRad = (((i + 1) * WEDGE_ARC_DEG - 90) * Math.PI) / 180;
      const accent = wedgeAccentColor(wedge);
      // Pie-slice path: centre → arc → back to centre.
      this.wedgeGfx
        .moveTo(0, 0)
        .arc(0, 0, radius, startRad, endRad)
        .lineTo(0, 0)
        .fill({ color: accent, alpha: 1 });
      // Thin separator stroke between wedges for crisp wedge edges.
      this.wedgeGfx
        .moveTo(
          Math.cos(startRad) * radius,
          Math.sin(startRad) * radius,
        )
        .lineTo(0, 0)
        .stroke({ color: 0x1a0e2a, alpha: 0.45, width: 1.5 });

      // Label at the mid-angle of the wedge, at labelRadius from
      // centre. Rotated to the wedge's own angle below, then spun with
      // the disc by labelHost (no counter-rotation) so it stays pinned
      // to its section.
      const midAngleRad =
        ((i + 0.5) * WEDGE_ARC_DEG - 90) * (Math.PI / 180);
      const lx = Math.cos(midAngleRad) * this.opts.labelRadius;
      const ly = Math.sin(midAngleRad) * this.opts.labelRadius;
      const text = new Text({
        text: wedgeGlyphText(wedge),
        style: {
          fontFamily: FONT_FAMILY,
          fontSize: this.opts.labelFontSize,
          fontWeight: '900',
          fill: 0xffffff,
          // Producer feedback 2026-05-17 — the "×2" stroke broke at
          // the corners of the multiplication sign. Pixi defaults to
          // miter joins which create gaps on sharp angles at small
          // font sizes; round joins keep the outline continuous.
          stroke: { color: 0x1a0e2a, width: 3, join: 'round' },
          letterSpacing: 0,
        },
      });
      text.anchor.set(0.5, 0.5);
      text.x = lx;
      text.y = ly;
      // Pin the glyph to its wedge: rotate by the wedge mid-angle so the
      // number reads radially (its "up" points out toward the rim). It
      // then spins with the disc via labelHost.angle in setRotation() —
      // producer 2026-06-10 wants the numbers locked to their sections,
      // not held upright.
      text.angle = (i + 0.5) * WEDGE_ARC_DEG;
      this.labelHost.addChild(text);
      this.labels.push(text);
    }
  }
}
